/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/* generated using openapi-typescript-codegen -- do no edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import { CancelError, type OnCancel } from './cancelable-promise-types';

export { CancelError } from './cancelable-promise-types';
export type { OnCancel } from './cancelable-promise-types';

/**
 * CancelablePromise class.
 */
export class CancelablePromise<T> implements Promise<T> {
    #isResolved: boolean;
    #isRejected: boolean;
    #isCancelled: boolean;
    readonly #cancelHandlers: (() => void)[];
    readonly #promise: Promise<T>;
    #resolve?: (value: T | PromiseLike<T>) => void;
    #reject?: (reason?: any) => void;

    // Helper method to check if promise is in a final state
    #isFinalState(): boolean {
        return this.#isResolved || this.#isRejected || this.#isCancelled;
    }

    // Helper method to create resolve handler
    #createResolveHandler(resolve: (value: T | PromiseLike<T>) => void): (value: T | PromiseLike<T>) => void {
        return (value: T | PromiseLike<T>): void => {
            if (this.#isFinalState()) {
                return;
            }
            this.#isResolved = true;
            resolve(value);
        };
    }

    // Helper method to create reject handler
    #createRejectHandler(reject: (reason?: any) => void): (reason?: any) => void {
        return (reason?: any): void => {
            if (this.#isFinalState()) {
                return;
            }
            this.#isRejected = true;
            reject(reason);
        };
    }

    // Helper method to create cancel handler
    #createCancelHandler(): OnCancel {
        const onCancel = (cancelHandler: () => void): void => {
            if (this.#isFinalState()) {
                return;
            }
            this.#cancelHandlers.push(cancelHandler);
        };

        Object.defineProperty(onCancel, 'isResolved', {
            get: (): boolean => this.#isResolved,
        });

        Object.defineProperty(onCancel, 'isRejected', {
            get: (): boolean => this.#isRejected,
        });

        Object.defineProperty(onCancel, 'isCancelled', {
            get: (): boolean => this.#isCancelled,
        });

        return onCancel as OnCancel;
    }

    constructor(
        executor: (
            resolve: (value: T | PromiseLike<T>) => void,
            reject: (reason?: any) => void,
            onCancel: OnCancel
        ) => void
    ) {
        this.#isResolved = false;
        this.#isRejected = false;
        this.#isCancelled = false;
        this.#cancelHandlers = [];
        this.#promise = new Promise<T>((resolve, reject) => {
            this.#resolve = resolve;
            this.#reject = reject;

            const onResolve = this.#createResolveHandler(resolve);
            const onReject = this.#createRejectHandler(reject);
            const onCancel = this.#createCancelHandler();

            return executor(onResolve, onReject, onCancel);
        });
    }

     get [Symbol.toStringTag]() {
            return "Cancellable Promise";
     }

    public then<TResult1 = T, TResult2 = never>(
        onFulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | null,
        onRejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
    ): Promise<TResult1 | TResult2> {
        return this.#promise.then(onFulfilled, onRejected);
    }

    public catch<TResult = never>(
        onRejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null
    ): Promise<T | TResult> {
        return this.#promise.catch(onRejected);
    }

    public finally(onFinally?: (() => void) | null): Promise<T> {
        return this.#promise.finally(onFinally);
    }

    // Helper method to check if cancellation is possible
    #canCancel(): boolean {
        return !(this.#isResolved || this.#isRejected || this.#isCancelled);
    }

    // Helper method to execute cancel handlers
    #executeCancelHandlers(): void {
        if (this.#cancelHandlers.length === 0) {
            return;
        }

        try {
            for (const cancelHandler of this.#cancelHandlers) {
                cancelHandler();
            }
        } catch (error) {
            console.warn('Cancellation threw an error', error);
        }
    }

    public cancel(): void {
        if (!this.#canCancel()) {
            return;
        }

        this.#isCancelled = true;
        this.#executeCancelHandlers();
        this.#cancelHandlers.length = 0;
        this.#reject?.(new CancelError('Request aborted'));
    }

    public get isCancelled(): boolean {
        return this.#isCancelled;
    }
}
